import * as Headless from '@headlessui/react';
import {
  BuildingOffice2Icon,
  CheckIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/auth-provider.tsx';
import { useSecurityContext } from '../../hooks/security-provider.tsx';
import type { AccountListModel } from '../../types/account.type.ts';
import { UserTypeEnum } from '../../types/user-type.ts';

const resultClassName = (isActive: boolean, isSelected: boolean) => {
  let stateClassName = 'hover:bg-white/80 focus:bg-white/80';
  if (isSelected) {
    stateClassName = 'bg-primary-50 ring-1 ring-inset ring-primary-300';
  }
  if (isActive) {
    stateClassName = 'bg-white shadow-md ring-2 ring-inset ring-primary-500';
  }

  return `group flex w-full items-center gap-4 px-2 py-5 text-left outline-none transition sm:px-4 ${stateClassName}`;
};

const AccountSwitcher = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setCurrentAccount, visibleAccounts: accounts, currentAccount } = useSecurityContext();
  const accountOptions = accounts ?? [];
  const showAccountSwitcher = accountOptions.length > 1;
  const isRep = user?.userType === UserTypeEnum.DrugRep;
  const [explorer, setExplorer] = useState({ isOpen: false, query: '', activeIndex: 0 });
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultListRef = useRef<HTMLDivElement>(null);

  const normalizedQuery = explorer.query.trim().toLocaleLowerCase();
  const filteredAccounts = accountOptions.filter(account => {
    const searchableText = `${account.accountName} ${account.organizationName}`.toLocaleLowerCase();
    return searchableText.includes(normalizedQuery);
  });
  const showAllAccountsOption =
    !normalizedQuery ||
    'all accounts'.includes(normalizedQuery) ||
    'every account'.includes(normalizedQuery);
  const searchResults: (AccountListModel | null)[] = showAllAccountsOption
    ? [null, ...filteredAccounts]
    : filteredAccounts;

  const selectedAccountName = currentAccount?.accountName ?? 'All accounts';
  const selectedOrganizationName = currentAccount?.organizationName ?? 'View every account';
  const resultLabel = `${filteredAccounts.length} account${filteredAccounts.length === 1 ? '' : 's'}`;
  const hasNoResults = !showAllAccountsOption && filteredAccounts.length === 0;
  let searchPlaceholder = 'Search Accounts or Organizations...';
  if (isRep) {
    searchPlaceholder = 'Search Connected Accounts';
  } else if (user?.userType === UserTypeEnum.StaffMember) {
    searchPlaceholder = 'Search Accounts';
  }

  useEffect(() => {
    if (accountOptions.length === 1) {
      setCurrentAccount(accountOptions[0]);
    }
  }, [accounts]);

  const closeExplorer = () => {
    setExplorer({ isOpen: false, query: '', activeIndex: 0 });
  };

  const goToAccountSearch = () => {
    const keyword = explorer.query.trim();
    const search = keyword ? `?keyword=${encodeURIComponent(keyword)}` : '';
    closeExplorer();
    navigate(`/connected-accounts${search}`);
  };

  const selectAccount = (account: AccountListModel | null) => {
    setCurrentAccount(account);
    closeExplorer();
  };

  const moveActiveResult = (direction: -1 | 1) => {
    if (searchResults.length === 0) {
      return;
    }

    const nextIndex =
      (explorer.activeIndex + direction + searchResults.length) % searchResults.length;
    setExplorer(current => ({ ...current, activeIndex: nextIndex }));
    requestAnimationFrame(() => {
      resultListRef.current
        ?.querySelector(`[data-result-index="${nextIndex}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    });
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveActiveResult(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (event.key === 'Enter' && searchResults.length > 0) {
      event.preventDefault();
      selectAccount(searchResults[explorer.activeIndex] ?? searchResults[0]);
    }
  };

  if (!showAccountSwitcher) {
    return null;
  }

  return (
    <>
      <button
        type='button'
        onClick={() => setExplorer({ isOpen: true, query: '', activeIndex: 0 })}
        className='group flex min-w-0 max-w-64 items-center gap-2.5 rounded-xl border border-zinc-200 bg-white px-2.5 py-2 text-left shadow-sm transition hover:border-primary-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2'
        aria-label={`Switch account. Current selection: ${selectedAccountName}`}
      >
        <span className='flex size-7 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 transition group-hover:bg-primary-100'>
          <BuildingOffice2Icon className='size-5' />
        </span>
        <span className='min-w-0 flex-1'>
          <span className='block truncate text-sm font-semibold leading-4 text-zinc-900'>
            {selectedAccountName}
          </span>
          <span className='mt-0.5 block truncate text-[11px] leading-3 text-zinc-500'>
            {selectedOrganizationName}
          </span>
        </span>
        <ChevronUpDownIcon className='size-4 shrink-0 text-zinc-400 transition group-hover:text-primary-600' />
      </button>

      <Headless.Dialog
        open={explorer.isOpen}
        onClose={closeExplorer}
        initialFocus={searchInputRef}
        className='relative z-50'
      >
        <Headless.DialogBackdrop
          transition
          className='fixed inset-0 bg-white/40 backdrop-blur-md transition duration-200 data-[closed]:opacity-0'
        />
        <div className='fixed inset-0 flex items-center justify-center p-4'>
          <Headless.DialogPanel
            transition
            className='flex h-[90dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white/70 shadow-2xl ring-1 ring-zinc-950/10 backdrop-blur-xl transition duration-200 ease-out data-[closed]:translate-y-4 data-[closed]:opacity-0'
          >
            <header className='border-b border-zinc-200/60 bg-white/50'>
              <div className='flex items-center justify-between px-5 py-4 sm:px-6'>
                <div>
                  <p className='text-xs font-semibold uppercase tracking-[0.16em] text-primary-600'>
                    Account explorer
                  </p>
                  <Headless.DialogTitle className='mt-0.5 text-lg font-semibold text-zinc-950'>
                    Switch account
                  </Headless.DialogTitle>
                </div>
                <button
                  type='button'
                  onClick={closeExplorer}
                  className='flex size-10 items-center justify-center rounded-lg text-zinc-500 transition hover:bg-zinc-100 hover:text-zinc-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                  aria-label='Close account explorer'
                >
                  <XMarkIcon className='size-6' />
                </button>
              </div>
            </header>

            <main className='min-h-0 flex-1 overflow-y-auto'>
              <div className='px-5 pb-10 pt-6 sm:px-6 sm:pt-8'>
                <div className='relative'>
                  <MagnifyingGlassIcon className='pointer-events-none absolute left-5 top-1/2 size-7 -translate-y-1/2 text-zinc-400 sm:left-7 sm:size-8' />
                  <input
                    ref={searchInputRef}
                    data-autofocus
                    type='search'
                    value={explorer.query}
                    onChange={event =>
                      setExplorer(current => ({
                        ...current,
                        query: event.target.value,
                        activeIndex: 0,
                      }))
                    }
                    onKeyDown={handleSearchKeyDown}
                    placeholder={searchPlaceholder}
                    className='h-20 w-full rounded-2xl border border-zinc-300 bg-white pl-16 pr-6 text-xl font-medium text-zinc-950 shadow-sm outline-none transition placeholder:text-zinc-400 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 sm:h-24 sm:pl-20 sm:pr-8 sm:text-3xl'
                    aria-label={searchPlaceholder}
                    role='combobox'
                    aria-autocomplete='list'
                    aria-expanded={explorer.isOpen}
                    aria-controls='account-explorer-results'
                    aria-activedescendant={
                      searchResults.length > 0
                        ? `account-explorer-result-${explorer.activeIndex}`
                        : undefined
                    }
                  />
                </div>

                <div className='mt-8 flex items-center justify-between border-b border-zinc-200 pb-3'>
                  <p className='text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500'>
                    Available accounts
                  </p>
                  <div className='flex items-center gap-4 text-xs text-zinc-400'>
                    <p className='hidden items-center gap-1.5 sm:flex'>
                      <kbd className='rounded border border-zinc-200 bg-white px-1.5 py-0.5 font-sans'>
                        ↑
                      </kbd>
                      <kbd className='rounded border border-zinc-200 bg-white px-1.5 py-0.5 font-sans'>
                        ↓
                      </kbd>
                      Navigate
                      <kbd className='ml-1 rounded border border-zinc-200 bg-white px-1.5 py-0.5 font-sans'>
                        Enter
                      </kbd>
                      Select
                    </p>
                    <p className='tabular-nums'>{resultLabel}</p>
                  </div>
                </div>

                <div
                  ref={resultListRef}
                  id='account-explorer-results'
                  role='listbox'
                  aria-label='Account search results'
                  className='divide-y divide-zinc-200'
                >
                  {showAllAccountsOption && (
                    <button
                      type='button'
                      role='option'
                      aria-selected={!currentAccount}
                      id='account-explorer-result-0'
                      data-result-index={0}
                      onClick={() => selectAccount(null)}
                      onMouseEnter={() => setExplorer(current => ({ ...current, activeIndex: 0 }))}
                      onFocus={() => setExplorer(current => ({ ...current, activeIndex: 0 }))}
                      className={resultClassName(explorer.activeIndex === 0, !currentAccount)}
                    >
                      <span className='flex size-12 shrink-0 items-center justify-center rounded-xl bg-zinc-900 text-sm font-bold text-white'>
                        ALL
                      </span>
                      <span className='min-w-0 flex-1'>
                        <span className='block truncate text-base font-semibold text-zinc-950'>
                          All accounts
                        </span>
                        <span className='mt-1 block truncate text-sm text-zinc-500'>
                          View every account
                        </span>
                      </span>
                      {!currentAccount && (
                        <span className='flex size-8 items-center justify-center rounded-full bg-primary-600 text-white'>
                          <CheckIcon className='size-5' />
                        </span>
                      )}
                    </button>
                  )}

                  {filteredAccounts.map((account, accountIndex) => {
                    const isSelected = currentAccount?.id === account.id;
                    const resultIndex = accountIndex + (showAllAccountsOption ? 1 : 0);
                    const isActive = explorer.activeIndex === resultIndex;

                    return (
                      <button
                        type='button'
                        role='option'
                        aria-selected={isSelected}
                        key={account.id}
                        id={`account-explorer-result-${resultIndex}`}
                        data-result-index={resultIndex}
                        onClick={() => selectAccount(account)}
                        onMouseEnter={() =>
                          setExplorer(current => ({ ...current, activeIndex: resultIndex }))
                        }
                        onFocus={() =>
                          setExplorer(current => ({ ...current, activeIndex: resultIndex }))
                        }
                        className={resultClassName(isActive, isSelected)}
                      >
                        <span className='flex size-12 shrink-0 items-center justify-center rounded-xl border border-zinc-200 bg-white text-primary-600 shadow-sm transition group-hover:border-primary-200 group-hover:bg-primary-50'>
                          <BuildingOffice2Icon className='size-6' />
                        </span>
                        <span className='min-w-0 flex-1'>
                          <span className='block truncate text-base font-semibold text-zinc-950'>
                            {account.accountName}
                          </span>
                          <span className='mt-1 block truncate text-sm text-zinc-500'>
                            {account.organizationName}
                          </span>
                        </span>
                        {isSelected && (
                          <span className='flex size-8 items-center justify-center rounded-full bg-primary-600 text-white'>
                            <CheckIcon className='size-5' />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {hasNoResults && isRep && (
                  <div className='mt-6 rounded-2xl border border-primary-200 bg-primary-50 px-5 py-6'>
                    <p className='text-base font-semibold text-zinc-950'>
                      No connected accounts match
                    </p>
                    <p className='mt-2 text-sm text-zinc-600'>
                      This switcher only shows accounts you already have access to. Search the
                      Accounts tab to find an account and send a request.
                    </p>
                    <Button
                      className='mt-4'
                      color='primary'
                      variant='solid'
                      onPress={goToAccountSearch}
                    >
                      Search Accounts
                    </Button>
                  </div>
                )}
                {hasNoResults && !isRep && (
                  <div className='py-20 text-center'>
                    <p className='text-lg font-semibold text-zinc-900'>No accounts found</p>
                    <p className='mt-1 text-sm text-zinc-500'>
                      Try searching by account or organization name.
                    </p>
                  </div>
                )}
              </div>
            </main>
          </Headless.DialogPanel>
        </div>
      </Headless.Dialog>
    </>
  );
};

export default AccountSwitcher;
